/**
 * Pure helpers behind NumericInput.
 *
 * `type="number"` is not a filter: by spec it accepts `e`, `E`, `+`, `-` and `.`,
 * and when the text is not a valid number the browser hands back `""` from
 * `e.target.value`. That is how a mistyped deposit silently became `null`.
 * These helpers keep the field a plain text box and police the characters
 * ourselves, so what the operator sees is always what the form receives.
 */

export interface NumericFormat {
  /** Digits allowed after the point. 0 makes the field integer-only. */
  decimals: number
  allowNegative: boolean
}

export interface NumericBounds {
  min?: number
  max?: number
}

/** Digits allowed after the point for an HTML `step`. `any` means unrestricted. */
export function decimalsForStep(step: number | string | undefined): number {
  if (step === undefined || step === null || step === '') return 0
  if (step === 'any') return 6
  const s = String(step)
  const dot = s.indexOf('.')
  return dot === -1 ? 0 : s.length - dot - 1
}

/**
 * Strip everything that cannot belong in the number the operator is typing,
 * while leaving part-typed values ("12.", "0.0", "-") intact so the field does
 * not fight the keyboard.
 */
export function sanitizeNumeric(raw: string, { decimals }: NumericFormat): string {
  // The sign is kept even when the field forbids negatives: dropping it turned
  // a typed or pasted "-500" into "500" with no word said. Blur then clamps it
  // to the field's floor and clampNote tells the operator.
  const negative = /^\s*-/.test(raw)
  let body = raw.replace(/[^\d.]/g, '')

  if (decimals <= 0) {
    body = body.replace(/\./g, '')
  } else {
    const dot = body.indexOf('.')
    if (dot !== -1) {
      const whole = body.slice(0, dot)
      const fraction = body
        .slice(dot + 1)
        .replace(/\./g, '')
        .slice(0, decimals)
      body = `${whole}.${fraction}`
    }
  }

  // "007" -> "7", but "0.5" and a lone "0" survive.
  body = body.replace(/^0+(?=\d)/, '')

  return negative ? `-${body}` : body
}

/**
 * The range actually enforced. A field that does not accept negatives has a
 * floor of zero even when no `min` was given, so a pasted `-5` clamps to `0`
 * rather than being flipped to `5` behind the operator's back.
 */
export function effectiveBounds({
  allowNegative,
  min,
  max,
}: Pick<NumericFormat, 'allowNegative'> & NumericBounds): NumericBounds {
  if (allowNegative) return { min, max }
  return { min: min === undefined ? 0 : Math.max(min, 0), max }
}

/**
 * What a field reports upstream WHILE the operator types. The display keeps the
 * typed text, but consumers parse `onChange` immediately, so they must never be
 * handed a lone sign or a number below the field's floor: a lone "-" reports
 * the empty string (not NaN fodder) and a value under the floor reports the
 * floor. The blur note then tells the operator what happened.
 */
export function emittedNumeric(
  clean: string,
  { allowNegative, min, max }: Pick<NumericFormat, 'allowNegative'> & NumericBounds,
): string {
  const parsed = parseFloat(clean)
  if (!isFinite(parsed)) return ''
  const bounds = effectiveBounds({ allowNegative, min, max })
  if (bounds.min !== undefined && parsed < bounds.min) return String(bounds.min)
  return clean
}

/**
 * Settle a part-typed value on blur: drop a dangling point or sign, clamp into
 * range, and round to the allowed precision.
 */
export function normalizeNumeric(
  raw: string,
  { decimals, allowNegative, min, max }: NumericFormat & NumericBounds,
): string {
  const parsed = parseFloat(raw)
  if (!isFinite(parsed)) return ''

  const bounds = effectiveBounds({ allowNegative, min, max })
  let value = parsed
  if (bounds.min !== undefined && value < bounds.min) value = bounds.min
  if (bounds.max !== undefined && value > bounds.max) value = bounds.max

  return decimals > 0 ? String(parseFloat(value.toFixed(decimals))) : String(Math.round(value))
}

/** Human sentence for a blur-time clamp, so the correction is never silent. */
export function clampNote(
  raw: string,
  settled: string,
  format: Pick<NumericFormat, 'allowNegative'> & NumericBounds,
): string | undefined {
  const typed = parseFloat(raw)
  if (!isFinite(typed) || settled === '') return undefined
  const { min, max } = effectiveBounds(format)
  if (min !== undefined && typed < min) {
    return `Lowest allowed is ${min}, so this was raised to ${min}.`
  }
  if (max !== undefined && typed > max) {
    return `Highest allowed is ${max}, so this was lowered to ${max}.`
  }
  return undefined
}

/** Increment used by the arrow keys. */
export function stepAmount(step: number | string | undefined, decimals: number): number {
  const parsed = typeof step === 'number' ? step : parseFloat(String(step ?? ''))
  if (isFinite(parsed) && parsed > 0) return parsed
  return decimals > 0 ? Number(`1e-${decimals}`) : 1
}
